import React, { useEffect, useRef, useState } from "react";
import ViatorBookingContactDetails from "./ViatorBookingContactDetails/ViatorBookingContactDetails";
import ViatorBookingPaymentDetails from "./ViatorBookingPaymentDetails/ViatorBookingPaymentDetails";
import Model, { Providers } from "@tripian/model";
import ViatorBookingActivityDetails from "./ViatorBookingActivityDetails/ViatorBookingActivityDetails";
import TravelSummary from "./TravelSummary/TravelSummary";
import { Modal, Button } from "@tripian/react";
import * as gtag from "../../../../gtag";
interface ICheckout {
  user?: Model.User;
  tourName: string;
  tourImage: string;
  travelDate?: string;
  travelHour?: string;
  travelPrice?: number;
  bookerInfo: Providers.Viator.BookerInfo;
  communication: Providers.Viator.Communication;
  bookingQuestions?: Providers.Viator.BookingQuestion[];
  bookingQuestionAnswers?: Providers.Viator.BookingQuestionAnswer[];
  languageGuides?: Providers.Viator.LanguageGuide[];
  logistics?: Providers.Viator.Logistics;
  paymentSessionToken?: string;
  travelerCount: number;
  locations: Providers.Viator.GroupedLocations;
  cancellationInfo?: string;
  flags?: string[];
  pickupIncluded: boolean;
  modalVisible: boolean;
  handleFetchBookingHold: () => void;
  clickedBookingContactDetails: ({
    bookerInfo,
    communication,
  }: {
    bookerInfo: Providers.Viator.BookerInfo;
    communication: Providers.Viator.Communication;
  }) => void;
  clickedBookingActivityDetails: ({
    bookingQuestionAnswers,
    languageGuide,
  }: {
    bookingQuestionAnswers: Providers.Viator.BookingQuestionAnswer[];
    languageGuide?: Providers.Viator.LanguageGuide;
  }) => void;
  setPaymentLoading: (loading: boolean) => void;
  onPaymentSuccess: (paymentToken: string) => Promise<boolean>;
  onPaymentError: (error: string) => void;
  t: (value: Model.TranslationKey) => string;
  onClose?: () => void;
}

const Checkout: React.FC<ICheckout> = ({
  user,
  tourName,
  tourImage,
  travelDate,
  travelHour,
  travelPrice,
  bookerInfo,
  communication,
  bookingQuestions,
  bookingQuestionAnswers,
  languageGuides,
  logistics,
  paymentSessionToken,
  travelerCount,
  locations,
  cancellationInfo,
  flags,
  pickupIncluded,
  modalVisible,
  handleFetchBookingHold,
  clickedBookingContactDetails,
  clickedBookingActivityDetails,
  setPaymentLoading,
  onPaymentSuccess,
  onPaymentError,
  t,
  onClose,
}) => {
  const [activeContainer, setActiveContainer] = useState(1); // Track which container is open
  const [isContactFormComplete, setIsContactFormComplete] = useState(false);

  // Refs for each section
  const contactRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);

  // Scroll to the active container when it changes
  useEffect(() => {
    if (activeContainer === 1 && contactRef.current) {
      const elementPosition =
        contactRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offset = -100;
      window.scrollTo({ top: elementPosition + offset, behavior: "smooth" });
    } else if (activeContainer === 2 && activityRef.current) {
      const elementPosition =
        activityRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offset = -100;
      window.scrollTo({ top: elementPosition + offset, behavior: "smooth" });
    }
  }, [activeContainer]);

  // Handler to go to the next container
  const handleNext = () => {
    if (activeContainer === 1) {
      setIsContactFormComplete(true);
      setActiveContainer(2);
    } else if (activeContainer === 2) {
      setActiveContainer(3);
    }
  };

  // Handler to open a specific container on Edit click
  const handleEdit = (containerNumber: number) => {
    setActiveContainer(containerNumber);
  };
  return (
    <div className="row relative mb0">
      <button
        className="m-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors hide-m"
        onClick={() => {
          gtag.googleAnalyticsEvent("booking_process_checkout_close", { provider: "viator" });
          onClose?.();
        }}
      >
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="feather feather-x"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="col col6-m col12 m0">
        <div className="flex flex-col gap-8 py-6">
          {/* Container 1: Contact Details */}
          <div className="pl-4" ref={contactRef}>
            <ViatorBookingContactDetails
              active={activeContainer === 1}
              clicked={({
                bookerInfo,
                communication,
              }: {
                bookerInfo: Providers.Viator.BookerInfo;
                communication: Providers.Viator.Communication;
              }) => {
                gtag.googleAnalyticsEvent("booking_process_contact_details_next", { provider: "viator" });
                clickedBookingContactDetails({ bookerInfo, communication });
                handleNext();
              }}
              user={user}
              bookerInfo={bookerInfo}
              communication={communication}
              handleEditContactDetails={() => {
                handleEdit(1);
                setIsContactFormComplete(false);
              }}
              t={t}
            />
          </div>

          {/* Container 2: Activity Details */}
          <div className="pl-4" ref={activityRef}>
            <ViatorBookingActivityDetails
              active={activeContainer === 2}
              clicked={({
                bookingQuestionAnswers,
                languageGuide,
              }: {
                bookingQuestionAnswers: Providers.Viator.BookingQuestionAnswer[];
                languageGuide?: Providers.Viator.LanguageGuide;
              }) => {
                gtag.googleAnalyticsEvent("booking_process_activity_details_next", { provider: "viator" });
                clickedBookingActivityDetails({
                  bookingQuestionAnswers,
                  languageGuide,
                });
                handleNext();
              }}
              handleEditActivityDetails={() => handleEdit(2)}
              bookingQuestions={bookingQuestions}
              questionAnswers={bookingQuestionAnswers}
              languageGuides={languageGuides}
              logistics={logistics}
              travelerCount={travelerCount}
              tourName={tourName}
              tourImage={tourImage}
              travelDate={travelDate}
              travelHour={travelHour}
              travelPrice={travelPrice}
              isEditable={isContactFormComplete}
              locations={locations}
              cancellationInfo={cancellationInfo}
              flags={flags}
              pickupIncluded={pickupIncluded}
              t={t}
            />
          </div>

          {/* Container 3: Payment */}
          <div className="pl-4">
            {activeContainer === 3 ? (
              <ViatorBookingPaymentDetails
                paymentRef={paymentRef}
                paymentSessionToken={paymentSessionToken}
                setPaymentLoading={setPaymentLoading}
                onSuccess={onPaymentSuccess}
                onError={onPaymentError}
                t={t}
              />
            ) : (
              <div className="row px-5 relative m-0 rounded-lg border-[1px] border-solid border-gray-300 min-h-20">
                <div className="col col12 my4 flex items-center gap-2">
                  <div className="rounded-full border-[1px] w-5 h-5 flex items-center justify-center border-black text-sm">
                    3
                  </div>
                  <h3 className="font-bold text-lg">
                    {t(
                      "trips.myTrips.localExperiences.tourDetails.paymentDetails"
                    )}
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col col6-m col12 m0 bg-[#f5f5f5] hide-s">
        <div className="sticky top-0 right-0 z-20 flex justify-end w-full bg-transparent">
          <button
            className="m-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            onClick={() => {
              gtag.googleAnalyticsEvent("booking_process_checkout_close", { provider: "viator" });
              onClose?.();
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-x"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="sticky top-28">
          <TravelSummary
            tourName={tourName}
            tourImage={tourImage}
            travelerCount={travelerCount}
            travelDate={travelDate}
            travelHour={travelHour}
            travelPrice={travelPrice}
            cancellationInfo={cancellationInfo}
            flags={flags}
            t={t}
          />
        </div>
      </div>
      {modalVisible && (
        <Modal
          show={modalVisible}
          className="booking-modal p5 w-full !min-w-[60%] !max-w-[90%] md:!max-w-[60%] md:!min-w-[30%] md:w-[40%] rounded-2xl shadow-xl"
        >
          <div className="row m0 flex flex-col">
            <div className="mb-4 font-bold text-xl mr-1">
              {t(
                "trips.myTrips.localExperiences.tourDetails.yourSessionExpired.title"
              )}
            </div>
            <div className="block mb-4">
              {t(
                "trips.myTrips.localExperiences.tourDetails.yourSessionExpired.desc"
              )}
            </div>
            <div>
              <Button
                color="primary"
                text="Continue Checkout"
                onClick={handleFetchBookingHold}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Checkout;
